import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Tooltip,
  IconButton,
  Snackbar,
  SnackbarContent,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-enterprise";
import { Col, Row } from "react-bootstrap";
import "./../../styles/userManagmentCommon.css";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { getMenusItemData } from "../../api/relationshipMenusApi/relationshipMenusApi";
import moment from "moment";
import { useSnackbar } from "../Common/SnackbarContext";
import { menuStatusChange } from "../../api/menuApi/menuApi";
import { DialogProps } from "../../types/SimpleSearchModel";
import ConfirmationDialog from "../SimpleSearch/ConfirmationDialog";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { appSettings } from "../../config/appSetting";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

interface MenuProps {
  businessUnitData: any;
}

const Menus: React.FC<MenuProps> = ({ businessUnitData }) => {
  const { showSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showEmptySearchMessage, setShowEmptySearchMessage] = useState(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [filteredStages, setFilteredStages] = useState<any[]>(rowData);
  const gridRef = useRef<AgGridReact>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { setLoading } = useLoader();
  const navigation = useNavigate();
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogProps>({
    dialogTitle: "",
    contentText: "",
    confirmText: "",
    closeText: "",
    onConfirm: null,
  });

  const handleActiveDeactiveClick = (menuId: number, active: boolean) => {
    if (active) {
      setDialogProps({
        dialogTitle: "Menus - Update Menu",
        contentText: "Are you sure you want to proceed?",
        confirmText: "Yes",
        closeText: "No",
        onConfirm: async () => {
          handleDeactivateClick(menuId);
          setDialogOpen(false);
        },
      });
      setDialogOpen(true);
    } else {
      handleDeactivateClick(menuId);
    }
  };

  const handleDeactivateClick = async (menuId: number) => {
    setLoading(true);
    try {
      const payload: any = {
        menuId: menuId,
        contextName: LocalStorageUtil.getItem("contextName") ?? "",
        updatedBy: LocalStorageUtil.getItem("userId") ?? "",
      };

      const response = await menuStatusChange(payload);
      if (response) {
        getData(menusModel);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const Actions = (props: any) => {
    if (props.data === undefined) {
      return "";
    }
    const handleEditAction = () => {
      const selectedRowData = props.data;
      navigation("/createMenuItem", {
        state: { rowData: selectedRowData, action: "Edit" },
      });
    };

    return (
      <>
        {isPagePermission("Allow Edit","Menu",1422) && (
          <Tooltip title="Click to edit the field">
            <EditIcon
              className="icon-button-other"
              onClick={handleEditAction}
              style={{ padding: "2px", color: "var(--aggrid-text-color)" }}
            />
          </Tooltip>
        )}
        {props.data?.active ? (
          <Tooltip title="Click to deactivate the Field">
            <PlayCircleIcon
              className="icon-button-other"
              onClick={() => handleActiveDeactiveClick(props.data.menuId, true)}
              style={{ padding: "2px", color: "var(--aggrid-text-color)" }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click to activate the Field">
            <DoDisturbIcon
              className="icon-button-other"
              style={{ padding: "2px", color: "var(--aggrid-text-color)" }}
              onClick={() =>
                handleActiveDeactiveClick(props.data.menuId, false)
              }
            />
          </Tooltip>
        )}
      </>
    );
  };

  const [columnDefs] = useState<ColDef[]>([
    
    {
      headerName: "#",
      width: 70,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "rowIndex",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
    },
    {
      headerName: "Menu Name",
      field: "menuName",
      tooltipField: "menuName",
      width: 270,
      flex: 1,
      colId: "menuName",
      sortable: true,
      filter: true,
      resizable: true,
      enableRowGroup: true,
    },
    {
      headerName: "Alias Name",
      field: "aliasName",
      tooltipField: "aliasName",
      width: 230,
      flex: 1,
      colId: "aliasName",
      sortable: true,
      filter: true,
      resizable: true,
      enableRowGroup: true,
    },
    {
      headerName: "Created On",
      field: "createdDate",
      tooltipField: "createdDate",
      width: 275,
      flex: 1,
      colId: "createdDate",
      sortable: true,
      filter: true,
      resizable: true,
      enableRowGroup: true,
      valueFormatter: (params) => {
        return params.value
          ? moment.utc(params.value).local().format("MM-DD-YYYY HH:mm:ss")
          : "";
      },
    },
    {
      headerName: "Updated On",
      field: "lastModifiedDate",
      tooltipField: "lastModifiedDate",
      width: 275,
      flex: 1,
      colId: "lastModifiedDate",
      sortable: true,
      filter: true,
      resizable: true,
      enableRowGroup: true,
      valueFormatter: (params) => {
        return params.value
          ? moment.utc(params.value).local().format("MM-DD-YYYY HH:mm:ss")
          : "";
      },
    },
    {
      headerName: "Updated By",
      valueGetter: (params) => {
        const { firstName, lastName } = params.data || {};
        return `${firstName || ""} ${lastName || ""}`.trim();
      },
      tooltipValueGetter: (params) => {
        const { firstName, lastName } = params.data || {};
        return `${firstName || ""} ${lastName || ""}`.trim();
      },
      width: 270,
      flex: 1,
      colId: "updatedBy",
      sortable: true,
      filter: true,
      resizable: true,
      enableRowGroup: true,
    },
    {
      headerName: "Actions",
      field: "link",
      cellRenderer: Actions,
      lockPinned: true,
      suppressSizeToFit: true,
      width: 100,
      minWidth: 100,
      pinned: "right",
      filter: false,
      resizable: false,
      sortable: false,
      enableRowGroup: false,
    },
  ]);

  const handleConfirmDialogClose = (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (confirmed) {
      console.log("Cancel confirmed");
    }
  };

  const handleCloseSearch = async () => {
    setSearchTerm("");
    setShowEmptySearchMessage(true);
    await getData(menusModel);
    setSnackbarOpen(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      setShowEmptySearchMessage(false);
    }
  };

  const handleSearchClick = async () => {
    if (!searchTerm) {
      setShowEmptySearchMessage(true);
      return;
    }
    let updateMenus: any = {
      ...menusModel,
      SearchText: searchTerm,
    };
    await getData(updateMenus);
    setShowEmptySearchMessage(false);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  let menusModel: any = {
    IsSuperAdmin: 1,
    SearchText: "",
    contextName:
      businessUnitData?.contextName || LocalStorageUtil.getItem("contextName"),
    currentPage: 1,
    isexporttpExcel: false,
    noOfRecords: 25,
  };

  const getData = async (updateMenus: any) => {
    setLoading(true);
    try {
      const menus = await getMenusItemData(updateMenus);
      setRowData(menus.processStageModel);
      setFilteredStages(menus.processStageModel);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData(menusModel);
  }, []);

  //Handle Export to excel
  const excelData = () => {
    if (gridRef.current) {
      const date = new Date();
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date
        .getDate()
        .toString()
        .padStart(2, "0")}-${date.getFullYear()}`;

      gridRef.current.api.exportDataAsExcel({
        fileName: `Menus_Reports_${formattedDate}.xlsx`,
        sheetName: "MenusReports",
        columnKeys: columnDefs
          .map((colDef) => colDef.field)// NOSONAR          
          .filter((field): field is string => typeof field === "string"),// NOSONAR
        processCellCallback: function (params) {
          return params.value;
        },
      });
      showSnackbar("File downloaded successfully!");
    }
  };

  //Quick Filter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);

  return (
    <div style={{ overflow: "hidden" }}>
      <NavigationBar
        title="Relationship Management"
        text="Menus"
        menuTitle="Configuration"
        screenTitle="Menus"
      />
      <ConfirmationDialog
        open={dialogOpen}
        onConfirm={dialogProps.onConfirm}
        onClose={setDialogOpen.bind(this, false)}
        contentText={dialogProps.contentText}
        closeText={dialogProps.closeText}
        confirmText={dialogProps.confirmText}
        dialogTitle={dialogProps.dialogTitle}
      />
      <Row style={{ marginLeft: "1em",marginTop: "1em" }}>
        <Col>
          <Box>
            <Snackbar
              open={snackbarOpen}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "100%", marginTop: "-23px" }}
            >
              <SnackbarContent
                className="snackbarContent"
                action={
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={handleSnackbarClose}
                  >
                    <CancelIcon fontSize="large" />
                  </IconButton>
                }
              />
            </Snackbar>
            <div className="searchContainer">
              <div className="searchWrapper">
                <div style={{ display: "none", alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="Search By Process Stages"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="searchInput"
                    data-testid="search-input"
                  />
                  <button
                    onClick={handleSearchClick}
                    className="searchButton"
                    title="Search"
                  >
                    <SearchIcon style={{ color: "#006ab6" }} />
                  </button>
                  {searchTerm && (
                    <IconButton
                      onClick={handleCloseSearch}
                      style={{ padding: "5px" }}
                    >
                      <CloseIcon
                        style={{ color: "var(--aggrid-text-color)" }}
                      />
                    </IconButton>
                  )}
                </div>
                <input
                  type="text"
                  id="filter-text-box"
                  placeholder="Quick Filter..."
                  style={{
                    width: "25em",
                    height: "2.5em",
                    fontSize: "11px",
                    paddingLeft: ".5em",
                    marginBottom: ".1em",
                  }}
                  onInput={onFilterTextBoxChanged}
                />
                {showEmptySearchMessage && (
                  <span className="errorMessage">
                    Please enter text for search
                  </span>
                )}
              </div>
              {isPagePermission("Allow Export To Excel","Menu",1421) && (
                <button
                  onClick={excelData}
                  className="excel-icon-button"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  aria-label="Download Excel"
                >
                  <img
                    src={ExcelIcon}
                    alt="Excel Icon"
                    className="excel-icon"
                  />
                </button>
              )}
            </div>
            <div className="rp-main-inner col-sm-12">
              <div className="queue-table-view clearfix">
                <div
                  className="ag-theme-quartz-dark"
                  style={{ height: "66vh",width: "99%" }}
                >
                  <AgGridReact
                    columnDefs={columnDefs}
                    rowData={filteredStages}
                    pagination={true}
                    paginationPageSize={defaulPageSize}
                    paginationPageSizeSelector={pageSizeSelector}
                    rowGroupPanelShow="always"
                    pivotPanelShow="always"
                    gridOptions={{
                      rowSelection: { 
                        mode : "multiRow",
                        checkboxes: true,
                        headerCheckbox: true,
                        enableClickSelection: false,
                       },  
                    }}
                    ref={gridRef}
                    loading={!rowData}
                    loadingOverlayComponent={CustomLoadingOverlay}
                    overlayNoRowsTemplate={
                      '<span class="ag-overlay-loading-center">No rows to show</span>'
                    }
                  />
                  <ConfirmDialog
                    open={confirmDialogOpen}
                    onClose={handleConfirmDialogClose}
                    title="Process Stages - Update Process Stages"
                    content="Are you sure you want to proceed?"
                  />
                </div>
              </div>
            </div>
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default Menus;
